import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Paper,
    Grid,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    Alert,
    CircularProgress,
    Autocomplete,
    TextField,
    Chip,
    Drawer,
    IconButton,
    Divider,
    Skeleton,
} from "@mui/material";
import { Close, FilterListOutlined } from "@mui/icons-material";
import styled from "@emotion/styled";
import { useHasPermission } from "../utils/permissionUtils";
import StatCard from "../components/StatCard";
import { getManagerReport, getManagerReportDrilldown } from "../redux/ducks/calendar";
import { getReportClients, getReportUsers } from "../redux/ducks/reports";
import AppDatePicker from "../components/AppDatePicker";
import { safeErrorMessage } from "../utils/errorUtils";
import { toLocalDay } from "../utils/dateUtils";

const Container = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    width: "100%",
}));

const FilterPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
}));

const FilterTitle = styled(Typography)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
}));

const FilterRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(160px, 1fr))",
    gap: theme.spacing(2),
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
    },
}));

const ActionsRow = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
}));

const SummaryGrid = styled(Grid)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const CategoryChipsRow = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
}));

const StyledTableCell = styled(TableCell, { shouldForwardProp: (prop) => prop !== "clickable" && prop !== "highlight" })(({ theme, clickable, highlight }) => ({
    cursor: clickable ? "pointer" : "default",
    color: highlight ? theme.palette.warning.main : "inherit",
    fontWeight: highlight ? theme.typography.fontWeightMedium : "inherit",
    "&:hover": clickable
        ? {
              backgroundColor: theme.palette.action.hover,
              textDecoration: "underline",
          }
        : {},
}));

const TotalsRow = styled(TableRow)(({ theme }) => ({
    backgroundColor: theme.palette.action.hover,
    "& td": {
        fontWeight: theme.typography.fontWeightSemiBold,
    },
}));

const DrilldownDrawer = styled(Drawer)(({ theme }) => ({
    "& .MuiDrawer-paper": {
        width: 480,
        maxWidth: "100%",
        padding: theme.spacing(2),
    },
}));

const DrilldownHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
}));

const DrilldownMeetingItem = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const CenteredLoading = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6),
}));

const isoDaysAgo = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return toLocalDay(d);
};
const isoToday = () => toLocalDay();

const fmtHrs = (n) => (Number(n) || 0).toFixed(2);

const normalizeForSearch = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const fuzzyFilter = (getLabel) => (options, { inputValue }) => {
    const q = normalizeForSearch(inputValue);
    if (!q) return options;
    return options.filter((o) => normalizeForSearch(getLabel(o)).includes(q));
};

const CATEGORIES = [
    { value: "all", label: "All" },
    { value: "internal", label: "Internal" },
    { value: "client", label: "Client" },
    { value: "misc", label: "Misc" },
];

const STATE_LABELS = {
    tracked: "Tracked",
    trackedElsewhere: "Tracked elsewhere",
    untracked: "Untracked",
};

const fmtMeetingDate = (m) => {
    if (!m?.start) return "";
    try {
        return new Date(m.start).toLocaleString();
    } catch {
        return String(m.start);
    }
};

const MeetingTimeReport = ({ startDate, endDate, onStartDateChange, onEndDateChange }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canManageCalendar = hasPermission("manageCalendar");

    const [reportState, setReportState] = useState({ rows: [], loading: false, error: null });
    const { rows, loading, error } = reportState;

    const from = startDate || isoDaysAgo(30);
    const to = endDate || isoToday();
    const setFrom = (v) => (onStartDateChange ? onStartDateChange(v) : null);
    const setTo = (v) => (onEndDateChange ? onEndDateChange(v) : null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [category, setCategory] = useState("all");

    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);

    const [drilldown, setDrilldown] = useState(null);
    const [drilldownLoading, setDrilldownLoading] = useState(false);
    const [drilldownMeetings, setDrilldownMeetings] = useState([]);

    useEffect(() => {
        if (!canManageCalendar) return;
        dispatch(getReportUsers((u) => setUsers(Array.isArray(u) ? u : [])));
        dispatch(getReportClients((c) => setClients(Array.isArray(c) ? c : [])));
    }, [dispatch, canManageCalendar]);

    const runReport = useCallback(() => {
        const query = { from, to, category: category === "all" ? undefined : category };
        if (selectedUsers.length > 0) query.userIds = selectedUsers.map((u) => u._id).join(",");
        if (selectedClient?._id) query.clientId = selectedClient._id;
        setReportState((prev) => ({ ...prev, loading: true, error: null }));
        dispatch(
            getManagerReport(
                query,
                (data) => setReportState({ rows: data?.rows || [], loading: false, error: null }),
                (err) => setReportState((prev) => ({ ...prev, loading: false, error: safeErrorMessage(err, "This report could not be loaded. Check the dates and run it again.") })),
            ),
        );
    }, [dispatch, from, to, category, selectedUsers, selectedClient]);

    useEffect(() => {
        if (canManageCalendar) runReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canManageCalendar, from, to]);

    const handleReset = () => {
        setSelectedUsers([]);
        setSelectedClient(null);
        setCategory("all");
    };

    const totals = useMemo(() => {
        const blank = { tracked: 0, trackedElsewhere: 0, untracked: 0 };
        const acc = {
            internal: { ...blank },
            client: { ...blank },
            misc: { ...blank },
            totals: { ...blank },
        };
        rows.forEach((r) => {
            ["internal", "client", "misc"].forEach((cat) => {
                const b = r[cat] || blank;
                acc[cat].tracked += b.tracked || 0;
                acc[cat].trackedElsewhere += b.trackedElsewhere || 0;
                acc[cat].untracked += b.untracked || 0;
            });
            acc.totals.tracked += r.totals?.tracked || 0;
            acc.totals.trackedElsewhere += r.totals?.trackedElsewhere || 0;
            acc.totals.untracked += r.totals?.untracked || 0;
        });
        return acc;
    }, [rows]);

    const openDrilldown = (row, cat, state) => {
        const value = row[cat]?.[state] || 0;
        if (!value) return;
        const ctx = { row, category: cat, state, value };
        setDrilldown(ctx);
        setDrilldownLoading(true);
        setDrilldownMeetings([]);
        const query = {
            userId: row.userId,
            from,
            to,
            category: cat,
            state,
        };
        dispatch(
            getManagerReportDrilldown(
                query,
                (data) => {
                    setDrilldownLoading(false);
                    setDrilldownMeetings(Array.isArray(data?.meetings) ? data.meetings : []);
                },
                () => {
                    setDrilldownLoading(false);
                }
            )
        );
    };

    const closeDrilldown = () => {
        setDrilldown(null);
        setDrilldownMeetings([]);
        setDrilldownLoading(false);
    };

    if (!canManageCalendar) {
        return (
            <Container>
                <Alert severity="warning">You do not have permission to view this report.</Alert>
            </Container>
        );
    }

    const renderHourCell = (row, cat, state) => {
        const value = row[cat]?.[state] || 0;
        const clickable = value > 0;
        return (
            <StyledTableCell
                align="right"
                clickable={clickable}
                highlight={state === "untracked" && value > 0}
                onClick={clickable ? () => openDrilldown(row, cat, state) : undefined}
            >
                {fmtHrs(value)}
            </StyledTableCell>
        );
    };

    return (
        <Container>
            <SummaryGrid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <StatCard
                        size="large"
                        tone={totals.totals.untracked > 0 ? "warn" : "neutral"}
                        label="Untracked Hours"
                        value={fmtHrs(totals.totals.untracked)}
                        sub={`across ${rows.length} user${rows.length === 1 ? "" : "s"}`}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard size="large" tone="success" label="Tracked Hours" value={fmtHrs(totals.totals.tracked)} sub="Recorded here" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard size="large" tone="info" label="Tracked Elsewhere" value={fmtHrs(totals.totals.trackedElsewhere)} sub="Recorded elsewhere" />
                </Grid>
            </SummaryGrid>

            <FilterPaper>
                <FilterTitle variant="subtitle1">
                    <FilterListOutlined fontSize="small" />
                    Filters
                </FilterTitle>
                <FilterRow>
                    <AppDatePicker label="From" size="small" value={from} onChange={(v) => setFrom(v)} fullWidth={false} />
                    <AppDatePicker label="To" size="small" value={to} onChange={(v) => setTo(v)} fullWidth={false} />
                    <Autocomplete
                        multiple
                        size="small"
                        options={users}
                        value={selectedUsers}
                        onChange={(_, v) => setSelectedUsers(v)}
                        getOptionLabel={(o) => o.fullName || `${o.first_name || ""} ${o.last_name || ""}`.trim() || o.email || ""}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        filterOptions={fuzzyFilter((o) => o?.fullName || `${o?.first_name || ""} ${o?.last_name || ""}`.trim() || o?.email || "")}
                        renderInput={(params) => <TextField {...params} label="Users" />}
                    />
                    <Autocomplete
                        size="small"
                        options={clients}
                        value={selectedClient}
                        onChange={(_, v) => setSelectedClient(v)}
                        getOptionLabel={(o) => o?.name || ""}
                        isOptionEqualToValue={(o, v) => o._id === v._id}
                        filterOptions={fuzzyFilter((o) => o?.name || "")}
                        renderInput={(params) => <TextField {...params} label="Client" />}
                    />
                </FilterRow>
                <CategoryChipsRow>
                    {CATEGORIES.map((c) => (
                        <Chip
                            key={c.value}
                            label={c.label}
                            color={category === c.value ? "primary" : "default"}
                            variant={category === c.value ? "filled" : "outlined"}
                            onClick={() => setCategory(c.value)}
                            size="small"
                        />
                    ))}
                </CategoryChipsRow>
                <ActionsRow>
                    <Button onClick={handleReset} variant="outlined">
                        Reset
                    </Button>
                    <Button onClick={runReport} variant="contained" disabled={loading}>
                        {loading ? "Loading..." : "Run report"}
                    </Button>
                </ActionsRow>
            </FilterPaper>

            {error && <Alert severity="error">{String(error)}</Alert>}

            <Paper>
                {loading ? (
                    <CenteredLoading>
                        <CircularProgress />
                    </CenteredLoading>
                ) : rows.length === 0 ? (
                    <Box p={4}>
                        <Alert severity="info">No meetings match these filters.</Alert>
                    </Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell rowSpan={2}>User</TableCell>
                                <TableCell align="center" colSpan={3}>Internal</TableCell>
                                <TableCell align="center" colSpan={3}>Client</TableCell>
                                <TableCell align="center" colSpan={3}>Misc</TableCell>
                                <TableCell align="center" colSpan={3}>Totals</TableCell>
                            </TableRow>
                            <TableRow>
                                {["internal", "client", "misc"].flatMap((cat) => [
                                    <TableCell key={`${cat}-tracked`} align="right">Tracked</TableCell>,
                                    <TableCell key={`${cat}-elsewhere`} align="right">Elsewhere</TableCell>,
                                    <TableCell key={`${cat}-untracked`} align="right">Untracked</TableCell>,
                                ])}
                                <TableCell align="right">Tracked</TableCell>
                                <TableCell align="right">Elsewhere</TableCell>
                                <TableCell align="right">Untracked</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={String(row.userId)} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={500}>{row.name || "Unknown"}</Typography>
                                        <Typography variant="caption" color="text.secondary">{row.email}</Typography>
                                    </TableCell>
                                    {renderHourCell(row, "internal", "tracked")}
                                    {renderHourCell(row, "internal", "trackedElsewhere")}
                                    {renderHourCell(row, "internal", "untracked")}
                                    {renderHourCell(row, "client", "tracked")}
                                    {renderHourCell(row, "client", "trackedElsewhere")}
                                    {renderHourCell(row, "client", "untracked")}
                                    {renderHourCell(row, "misc", "tracked")}
                                    {renderHourCell(row, "misc", "trackedElsewhere")}
                                    {renderHourCell(row, "misc", "untracked")}
                                    <TableCell align="right">{fmtHrs(row.totals?.tracked)}</TableCell>
                                    <TableCell align="right">{fmtHrs(row.totals?.trackedElsewhere)}</TableCell>
                                    <StyledTableCell align="right" highlight={(row.totals?.untracked || 0) > 0}>
                                        {fmtHrs(row.totals?.untracked)}
                                    </StyledTableCell>
                                </TableRow>
                            ))}
                            <TotalsRow>
                                <TableCell>Totals</TableCell>
                                {["internal", "client", "misc"].flatMap((cat) => [
                                    <TableCell key={`tot-${cat}-tracked`} align="right">{fmtHrs(totals[cat].tracked)}</TableCell>,
                                    <TableCell key={`tot-${cat}-elsewhere`} align="right">{fmtHrs(totals[cat].trackedElsewhere)}</TableCell>,
                                    <TableCell key={`tot-${cat}-untracked`} align="right">{fmtHrs(totals[cat].untracked)}</TableCell>,
                                ])}
                                <TableCell align="right">{fmtHrs(totals.totals.tracked)}</TableCell>
                                <TableCell align="right">{fmtHrs(totals.totals.trackedElsewhere)}</TableCell>
                                <TableCell align="right">{fmtHrs(totals.totals.untracked)}</TableCell>
                            </TotalsRow>
                        </TableBody>
                    </Table>
                )}
            </Paper>

            <DrilldownDrawer anchor="right" open={!!drilldown} onClose={closeDrilldown}>
                <DrilldownHeader>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={500}>
                            {drilldown?.row?.name || "User"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {drilldown ? `${drilldown.category} · ${STATE_LABELS[drilldown.state] || drilldown.state} · ${fmtHrs(drilldown.value)} hrs` : ""}
                        </Typography>
                    </Box>
                    <IconButton onClick={closeDrilldown}><Close /></IconButton>
                </DrilldownHeader>
                <Divider />
                <Box mt={2}>
                    {drilldownLoading ? (
                        <>
                            <Skeleton height={64} />
                            <Skeleton height={64} />
                            <Skeleton height={64} />
                        </>
                    ) : drilldownMeetings.length === 0 ? (
                        <Alert severity="info">No meetings.</Alert>
                    ) : (
                        drilldownMeetings.map((m) => (
                            <DrilldownMeetingItem key={m._id}>
                                <Typography variant="body2" fontWeight={500}>{m.title || "(no title)"}</Typography>
                                <Typography variant="caption" color="text.secondary" display="block">{fmtMeetingDate(m)}</Typography>
                                {Array.isArray(m.attendees) && m.attendees.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Attendees: {m.attendees.map((a) => a.name || a.email).filter(Boolean).join(", ")}
                                    </Typography>
                                )}
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {m.timeEntryId ? "Tracked" : m.manuallyTracked ? "Tracked elsewhere" : "Untracked"}
                                    {m.cancelled ? " · Cancelled" : ""}
                                </Typography>
                            </DrilldownMeetingItem>
                        ))
                    )}
                </Box>
            </DrilldownDrawer>
        </Container>
    );
};

export default MeetingTimeReport;
